import { useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";
import ObjectUtils from "../utils/ObjectUtils";

type PagingData = {
  rows: any[];
  nextOffset: number|null;
};

type VirtualTableType = {
  key: string;
  source: any[];
  limit: number;
  itemHeight: number;
};

const fetchPage = async ({ source, limit, offset = 0 } : {
  source: any[], limit: number, offset: number
}): Promise<PagingData> => await new Promise((resolve) => {
  const startIndex = offset * limit;
  const endIndex = Math.min((offset + 1) * limit, source.length);

  const timeOut = setTimeout(() => {
    clearTimeout(timeOut);
    if (endIndex >= source.length)
      resolve({ rows: source.slice(startIndex, endIndex), nextOffset: null });

    resolve({
      rows: source.slice(startIndex, startIndex + limit),
      nextOffset: ((endIndex >= source.length) ? null : offset + 1) ?? offset,
    });
}, 100)});

const useVirtualTable = ({ key, source, limit, itemHeight }: VirtualTableType) => {
  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, refetch }
    = useInfiniteQuery<PagingData, Error>([key],
      ({ pageParam = 0 }) => fetchPage({source, limit, offset: pageParam }), {
        getNextPageParam: (lastPage) => lastPage.nextOffset,
    });
	const tableContainerRef = useRef<HTMLDivElement|null>(null);
  const sourceRef = useRef<any[]>([]);

  const virtualRowsRef = useRef<HTMLDivElement|null>(null);
  const virtualHeaderRef = useRef<HTMLDivElement|null>(null);

  const allRows = useMemo(() => !!data ? data?.pages.flatMap(page => page.rows)
    ?.filter((item, index, self) => self?.findIndex(i => i?.id === item?.id) === index) : []
  , [data]);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? allRows.length + 1 : allRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => itemHeight,
    overscan: 5,
  });

  useEffect(() => {
    if (!ObjectUtils.equalObjects(source, sourceRef.current)) {
      Promise.resolve(refetch());
      sourceRef.current = structuredClone(source);
    }
  }, [refetch, source]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem || !hasNextPage || isFetchingNextPage)
      return;

    if (lastItem.index >= allRows.length - 1)
      fetchNextPage();
  // eslint-disable-next-line
  }, [hasNextPage, fetchNextPage, allRows.length, isFetchingNextPage, rowVirtualizer.getVirtualItems()]);

  return {
    allRows,
    rowVirtualizer,
    hasNextPage,
    tableContainerRef,
    virtualHeaderRef,
    virtualRowsRef,
  };
}

export default useVirtualTable;